import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  runTransaction,
  Timestamp,
  increment,
  addDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functionsClient } from './firebase';
import { AppUser, AdminPermissions, UserSuspension, AdminRole } from '../types';
import type { SuspensionReasonCategory } from '../../functions/src/moderationConfig';

const SUPER_ADMIN_EMAIL = 'ajaytripathi821@gmail.com';

const ensureAdminAccess = async (requiredRole: 'any' | 'super' = 'any', targetUid?: string) => {
  const adminUid = auth.currentUser?.uid;
  if (!adminUid) {
    throw new Error('Not authenticated.');
  }

  const adminDoc = await getDoc(doc(db, 'users', adminUid));
  const admin = (adminDoc.exists() ? adminDoc.data() : null) as AppUser | null;

  if (!admin || !admin.role) {
    throw new Error('Not authorized to manage admin actions.');
  }

  const isSuperAdmin = admin.role === 'superadmin' && admin.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  const isSubAdmin = admin.role === 'subadmin';

  if (requiredRole === 'super' && !isSuperAdmin) {
    throw new Error('Super admin access is required.');
  }

  if (!isSuperAdmin && !isSubAdmin) {
    throw new Error('This user is not an administrator.');
  }

  if (targetUid && isSubAdmin) {
    const targetDoc = await getDoc(doc(db, 'users', targetUid));
    const target = (targetDoc.exists() ? targetDoc.data() : null) as AppUser | null;
    const targetIsProtectedAdmin = target?.role === 'superadmin' || target?.role === 'subadmin';
    if (targetIsProtectedAdmin) {
      throw new Error('Sub-admins cannot suspend or modify other admin accounts.');
    }
  }

  return { adminUid, admin, isSuperAdmin, isSubAdmin };
};

/**
 * Super Admin Check
 */
export const checkIsSuperAdmin = async (uid: string): Promise<boolean> => {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) return false;
  const data = userDoc.data() as AppUser;
  return data.role === 'superadmin' && data.email === SUPER_ADMIN_EMAIL;
};

/**
 * Unique ClueID Generation (Section 3)
 * Ranges:
 * 1-100: Company (Manual)
 * 101-20000: Personalities (Manual)
 * 20001+: Regular (Auto)
 */
export const generateAutoClueId = async (uid: string): Promise<string> => {
  if (auth.currentUser?.uid !== uid) {
    throw new Error('Sign in to generate your Clue ID.');
  }
  const result = await httpsCallable<undefined, { clueId: string }>(
    functionsClient,
    'generateAutoClueId'
  )(undefined);
  return result.data.clueId;
};

/**
 * Manual ClueID Assignment (Admins Only)
 */
export const assignManualClueId = async (uid: string, numericId: number, reason: string) => {
  if (!Number.isInteger(numericId) || numericId < 1 || numericId > 20000) {
    throw new Error('Clue ID must be an integer between 1 and 20000.');
  }
  if (!reason.trim()) throw new Error('A reason is required to assign a Clue ID.');
  await httpsCallable(functionsClient, 'assignManualClueId')({
    uid,
    numericId,
    reason: reason.trim()
  });
};

/**
 * Suspension Logic (Section 2.3)
 */
export const suspendUser = async (
  uid: string,
  durationDays: number,
  reasonCategory: SuspensionReasonCategory,
  reasonDetails: string,
  referenceId = '',
  evidence: { reportIds?: string[]; blockCount?: number } = {}
) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Sign in with an administrator account.');
  }
  if (uid === user.uid) {
    throw new Error('Admins cannot suspend their own account.');
  }
  if (!Number.isInteger(durationDays) || durationDays < 0) {
    throw new Error('Enter a valid suspension duration in days.');
  }
  await httpsCallable(functionsClient, 'suspendUser')({
    uid,
    durationDays,
    reasonCategory,
    reasonDetails,
    referenceId,
    ...evidence
  });
};

export const liftSuspension = async (uid: string, reason: string) => {
  await httpsCallable(functionsClient, 'liftSuspension')({
    uid,
    reason: reason.trim()
  });
};

/**
 * Sub-Admin Management
 */
export const setAdminRole = async (uid: string, role: AdminRole, permissions?: AdminPermissions) => {
  const reason = window.prompt('Reason for this role change (required):')?.trim();
  if (!reason) throw new Error('A reason is required for role changes.');
  await httpsCallable(functionsClient, 'setStaffRole')({
    uid,
    role,
    reason,
    permissions
  });
};

export const listAdminDirectory = async () => {
  const result = await httpsCallable<undefined, { users: Record<string, unknown>[] }>(
    functionsClient,
    'listAdminDirectory'
  )(undefined);
  return result.data.users;
};

export const listAdminMedia = async () => {
  const result = await httpsCallable<undefined, { media: Record<string, unknown>[] }>(
    functionsClient,
    'listAdminMedia'
  )(undefined);
  return result.data.media;
};

export const listModerationQueues = async () => {
  const result = await httpsCallable<undefined, {
    suspensions: Array<{ uid: string; suspension: UserSuspension; evidence: Record<string, unknown> | null }>;
    appeals: Array<Record<string, unknown> & { id: string }>;
  }>(functionsClient, 'listModerationQueues')(undefined);
  return result.data;
};

export const reviewSuspension = async (
  uid: string,
  action: 'approve' | 'adjust' | 'revoke',
  reason: string,
  durationDays?: number
) => {
  await httpsCallable(functionsClient, 'reviewSuspension')({ uid, action, reason, durationDays });
};

export const reviewSuspensionAppeal = async (
  appealId: string,
  decision: 'review' | 'upheld' | 'revoked',
  reason: string
) => {
  await httpsCallable(functionsClient, 'reviewSuspensionAppeal')({ appealId, decision, reason });
};

export const deleteUserAccount = async (uid: string, reason: string) => {
  await httpsCallable(functionsClient, 'deleteUserAccount')({ uid, reason });
};

export const moderateContent = async (contentId: string, action: 'hide' | 'delete', reason: string) => {
  await httpsCallable(functionsClient, 'moderateContent')({ contentId, action, reason });
};


/**
 * Secure Logger (Section 6)
 */
export const logAdminAction = async (action: string, details: any) => {
  const adminUid = auth.currentUser?.uid;
  await addDoc(collection(db, 'logs'), {
    adminUid,
    action,
    details,
    timestamp: serverTimestamp()
  });
};

/**
 * CSV Export Logic (Conceptual Implementation for Segmentation)
 */
export const generateSegmentedExport = async (
    collectionName: 'users' | 'media',
    onFileReady: (csvContent: string, part: number) => void
) => {
    const PAGE_SIZE = 500;
    const SEGMENT_SIZE = 500000;
    let count = 0;
    let part = 1;
    let currentBatch: any[] = [];
    let cursor: QueryDocumentSnapshot | undefined;

    while (true) {
      const constraints = [
        orderBy('createdAt', 'asc'),
        ...(cursor ? [startAfter(cursor)] : []),
        limit(PAGE_SIZE)
      ];
      const snapshot = await getDocs(query(collection(db, collectionName), ...constraints));
      snapshot.forEach(docSnap => {
          const data = docSnap.data();
          if (collectionName === 'users') {
            currentBatch.push({
              id: docSnap.id,
              ...data,
              totalViews: data.stats?.totalViews ?? data.totalViews ?? 0,
              totalLikes: data.stats?.totalLikes ?? data.totalLikes ?? 0
            });
          } else {
            const { category: _category, ...contentData } = data;
            currentBatch.push({ id: docSnap.id, ...contentData });
          }
          count++;

          if (count >= SEGMENT_SIZE) {
              onFileReady(convertToCSV(currentBatch), part);
              part++;
              count = 0;
              currentBatch = [];
          }
      });
      if (snapshot.size < PAGE_SIZE) break;
      cursor = snapshot.docs[snapshot.docs.length - 1];
    }

    if (currentBatch.length > 0) {
        onFileReady(convertToCSV(currentBatch), part);
    }
};

const convertToCSV = (data: any[]) => {
    if (data.length === 0) return '';
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(obj =>
        Object.values(obj).map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')
    );
    return [headers, ...rows].join('\n');
};
